import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Layout from 'containers/Layout'
import moment from 'moment'
import { get, includes } from 'lodash'
import apiGet from 'lib/network/apiGet'
import Table from 'components/pageComponents/Table'
import LoadingIcon from 'components/atoms/LoadingIcon'
import EmptyState from 'components/pageComponents/EmptyState'
import Button from 'components/atoms/Button'
import { formateDate } from 'utils'
import notify from 'components/atoms/Notify'
import BranchDiscounts from './modals/branchDiscountModal'

import { Tooltip } from 'antd'
import classNames from 'classnames'

import 'react-date-range/dist/styles.css'
import 'react-date-range/dist/theme/default.css'

import PageFooter from 'components/paginationComponent'
import Selection from 'components/molecules/Selection'
import apiPost from 'lib/network/apiPost'
import useModal from 'hooks/useModal'

const BranchDiscount = () => {
  const { t, i18n } = useTranslation()
  const [filter, setFilter] = useState({ workspace: -1 })

  const [loading, setLoading] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [pagination, setPagination] = useState({})
  const [branchDiscounts, setBranchDiscounts] = useState([])
  const [companyNames, setCompanyNames] = useState([])
  const [branches, setBranches] = useState([])
  const [selectedCompany, setSelectedCompany] = useState(null)
  const [selectedBranch, setSelectedBranch] = useState(null)
  const [loadingCompanies, setLoadingCompanies] = useState(false)
  const [loadingBranches, setLoadingBranches] = useState(false)

  const [selectedRentType, setSelectedRentType] = useState([])
  const [discountStartDate, setDiscountStartDate] = useState('')
  const [discountEndDate, setDiscountEndDate] = useState('')
  const [ajarlePercentage, setAjarlePercentage] = useState('')
  const [companyPercentage, setCompanyPercentage] = useState('')
  const [ajarleeDeliveryDiscount, setAjarleeDeliveryDiscount] = useState('')
  const [companyDeliveryDiscount, setCompanyDeliveryDiscount] = useState('')
  const [companyMaxAjarleePercentage, setCompanyMaxAjarleePercentage] = useState(null)
  const [fieldError, setFieldError] = useState(null)

  // Validation errors show under the offending field, like the reset modal
  const fail = (field, message) => setFieldError({ field, message })
  const clearError = field => setFieldError(prev => (prev?.field === field ? null : prev))
  const errorText = field => fieldError?.field === field && <p className="text-red-600 text-sm mt-1">{fieldError.message}</p>
  const inputClass = field =>
    classNames(
      'w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base',
      fieldError?.field === field ? 'border-red-500' : 'border-gray-300'
    )
  const isBlank = value => value === '' || value == null

  const rentalTypes = [
    { id: 'dailyRate', name: t('branchDiscount.rentalTypes.dailyRate') },
    { id: 'weeklyRate', name: t('branchDiscount.rentalTypes.weeklyRate') },
    { id: 'monthlyRate', name: t('branchDiscount.rentalTypes.monthlyRate') },
    { id: 'yearlyRate', name: t('branchDiscount.rentalTypes.yearlyRate') },
  ]

  useEffect(() => {
    fetchBranchDiscounts()
  }, [filter])

  const fetchBranchDiscounts = async (page = 1) => {
    if (page === 1) {
      setLoading(true)
    }

    const params = {}
    params.pageNumber = page
    params.pageSize = 20
    if (filter.sortBy && filter.sortOrder) {
      params.sortBy = filter.sortBy
      params.sortOrder = filter.sortOrder
    }

    try {
      const response = await apiGet('/branch/Discounts', params)
      const list = response?.data?.branchDiscount || []
      setBranchDiscounts(page === 1 ? list : [...branchDiscounts, ...list])
      setPagination(response?.data?.pagination || {})
    } catch (error) {
      notify.error(t('branchDiscount.messages.fetchDiscountsFailed'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchCompanies()
  }, [])

  const fetchCompanies = async () => {
    setLoadingCompanies(true)
    const response = await apiGet('companies', { pageSize: 1000 })
    if (response?.data) {
      const companies = response.data.company.map(company => ({
        id: company._id,
        name: company.name,
        ajarleePercentage: company.ajarleePercentage,
      }))
      setCompanyNames(companies)
    } else {
      setCompanyNames([])
    }
    setLoadingCompanies(false)
  }

  const fetchBranches = async companyId => {
    try {
      setLoadingBranches(true)
      const response = await apiGet(`company/${companyId}/branches`)
      if (response?.data) {
        const branchList = response.data.map(branch => ({
          id: branch._id,
          name: branch.name,
        }))
        setBranches(branchList)
      } else {
        setBranches([])
      }
      setLoadingBranches(false)
    } catch (error) {
      notify.error(t('branchDiscount.messages.fetchBranchesFailed'))
      setBranches([])
      setLoadingBranches(false)
    }
  }

  const handleCompanyChange = value => {
    setSelectedCompany(value)
    setSelectedBranch(null)
    clearError('company')
    // eslint-disable-next-line
    const selectedCompanyData = companyNames.find(company => company.id === value)
    setCompanyMaxAjarleePercentage(selectedCompanyData?.ajarleePercentage ?? null)

    if (value) {
      fetchBranches(value)
    } else {
      setBranches([])
      setCompanyMaxAjarleePercentage(null)
    }
  }

  // Multi-select Listbox hands back the full array of selected ids
  const handleRentTypeChange = ids => {
    setSelectedRentType(ids || [])
    clearError('rentType')
  }

  const handleSetDiscount = async () => {
    if (!selectedCompany) return fail('company', t('branchDiscount.messages.selectCompany'))
    if (!selectedBranch) return fail('branch', t('branchDiscount.messages.selectBranch'))
    if (selectedRentType.length === 0) return fail('rentType', t('branchDiscount.messages.selectRentalPeriod'))
    if (!discountStartDate) return fail('discountStartDate', t('branchDiscount.messages.selectStartDate'))
    if (!discountEndDate) return fail('discountEndDate', t('branchDiscount.messages.selectEndDate'))
    if (new Date(discountEndDate) <= new Date(discountStartDate)) return fail('discountEndDate', t('branchDiscount.messages.endBeforeStart'))
    if (isBlank(ajarlePercentage) || Number(ajarlePercentage) < 0) return fail('ajarlePercentage', t('branchDiscount.messages.validAjarleePercentage'))
    if (companyMaxAjarleePercentage != null && Number(ajarlePercentage) > companyMaxAjarleePercentage) {
      return fail('ajarlePercentage', t('branchDiscount.messages.ajarleePercentageExceeded', { max: companyMaxAjarleePercentage }))
    }
    if (isBlank(companyPercentage) || Number(companyPercentage) < 0) return fail('companyPercentage', t('branchDiscount.messages.validCompanyPercentage'))
    const totalPercentage = Number(ajarlePercentage) + Number(companyPercentage)
    if (totalPercentage >= 100) return fail('companyPercentage', t('branchDiscount.messages.totalPercentageExceeded', { total: totalPercentage }))
    if (!isBlank(ajarleeDeliveryDiscount) && (ajarleeDeliveryDiscount < 0 || ajarleeDeliveryDiscount > 100)) {
      return fail('ajarleeDeliveryDiscount', t('branchDiscount.messages.ajarleeDeliveryDiscountRange'))
    }
    if (!isBlank(companyDeliveryDiscount) && (companyDeliveryDiscount < 0 || companyDeliveryDiscount > 100)) {
      return fail('companyDeliveryDiscount', t('branchDiscount.messages.companyDeliveryDiscountRange'))
    }
    setFieldError(null)

    try {
      setGenerating(true)

      const requestData = {
        rentTypes: selectedRentType,
        discountStartDate,
        discountEndDate,
        branchId: selectedBranch,
        companyId: selectedCompany,
        ajarleePercentage: ajarlePercentage,
        companyPercentage,
        ajarleeDeliveryDiscount,
        companyDeliveryDiscount,
      }

      const response = await apiPost('branch/discount', requestData)

      if (response?.success) {
        notify.success(t('branchDiscount.messages.discountSetSuccess'))
        setSelectedCompany(null)
        setSelectedBranch(null)
        setSelectedRentType([])
        setDiscountStartDate('')
        setDiscountEndDate('')
        setAjarlePercentage('')
        setCompanyPercentage('')
        setAjarleeDeliveryDiscount('')
        setCompanyDeliveryDiscount('')
        setBranches([])
        setCompanyMaxAjarleePercentage(null)
        await fetchBranchDiscounts(1)
      } else {
        notify.error(response?.message || t('branchDiscount.messages.discountSetFailed'))
      }
    } catch (error) {
      notify.error(t('branchDiscount.messages.discountSetFailed'))
    } finally {
      setGenerating(false)
    }
  }

  const columns = [
    {
      width: '10%',
      name: t('branchDiscount.columns.generatedBy'),
      key: 'generatedBy',
      sortable: true,
      rendered: item => <span className="text-[13px]">{item?.createdBy?.fullName || t('branchDiscount.labels.admin')}</span>,
    },
    {
      width: '10%',
      name: t('branchDiscount.columns.rentType'),
      key: 'rentType',
      sortable: true,
      rendered: item => {
        return (
          <div className="flex overflow-x-auto whitespace-nowrap gap-1">
            {item?.rentTypes?.map((type, index) => {
              const isReset = includes(item?.resetRentTypes, type)
              return (
                <span
                  key={index}
                  title={isReset ? t('branchDiscount.labels.reset') : undefined}
                  className={classNames('text-xs px-2 py-1 rounded-full', isReset ? 'bg-gray-100 text-gray-400 line-through' : 'bg-gray-200 text-gray-700')}
                >
                  {t(`branchDiscount.rentalTypes.${type}`, type)}
                </span>
              )
            })}
          </div>
        )
      },
    },

    {
      width: '10%',
      name: t('branchDiscount.columns.company'),
      key: 'company',
      sortable: true,
      rendered: item => {
        return (
          <>
            <div>
              <span className="text-[13px]">{item?.company?.name}</span>
            </div>
          </>
        )
      },
    },
    {
      width: '10%',
      name: t('branchDiscount.columns.branch'),
      key: 'branch',
      sortable: true,
      rendered: item => {
        return (
          <>
            <div>
              <span className="text-[13px]">{item?.branch?.name}</span>
            </div>
          </>
        )
      },
    },
    {
      width: '10%',
      name: t('branchDiscount.columns.companyPercentage'),
      key: 'companyPercentage',
      sortable: true,
      rendered: item => {
        return (
          <>
            <div>
              <span className="text-[13px]">{item?.companyPercentage || 0}%</span>
            </div>
          </>
        )
      },
    },
    {
      width: '10%',
      name: t('branchDiscount.columns.ajarleePercentage'),
      key: 'ajarleePercentage',
      sortable: true,
      rendered: item => {
        return (
          <>
            <div>
              <span className="text-[13px]">{item?.ajarleePercentage || 0}%</span>
            </div>
          </>
        )
      },
    },
    {
      width: '10%',
      name: t('branchDiscount.columns.ajarleeDeliveryDiscount'),
      key: 'ajarleeDeliveryDiscount',
      sortable: true,
      rendered: item => {
        return (
          <>
            <div>
              <span className="text-[13px]">{item?.ajarleeDeliveryDiscount || 0}%</span>
            </div>
          </>
        )
      },
    },
    {
      width: '10%',
      name: t('branchDiscount.columns.companyDeliveryDiscount'),
      key: 'companyDeliveryDiscount',
      sortable: true,
      rendered: item => {
        return (
          <>
            <div>
              <span className="text-[13px]">{item?.companyDeliveryDiscount || 0}%</span>
            </div>
          </>
        )
      },
    },
    {
      width: '10%',
      name: t('branchDiscount.columns.dateRange'),
      key: 'range',
      sortable: true,
      rendered: item => {
        const formatDate = dateStr => {
          if (!dateStr) return ''
          const date = new Date(dateStr)
          return date.toLocaleString(i18n.language === 'ar' ? 'ar' : 'en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
          })
        }

        return (
          <div>
            <span className="text-[13px]">
              {formatDate(item?.discountStartDate)} - {formatDate(item?.discountEndDate)}
            </span>
          </div>
        )
      },
    },

    {
      width: '5%',
      name: t('branchDiscount.columns.created'),
      key: 'created',
      sortable: true,
      rendered: item => {
        const createdTime = get(item, 'createdAt', '')
        const createdAt = createdTime ? moment(createdTime).fromNow() : '-'

        return (
          <div className="text-sm text-gray-500">
            <Tooltip title={createdTime ? formateDate(moment(createdTime)) : t('branchDiscount.labels.notAvailable')}>{createdAt}</Tooltip>
          </div>
        )
      },
    },

    {
      width: '5%',
      name: t('branchDiscount.columns.updated'),
      key: 'updated',
      sortable: true,
      rendered: item => {
        const updatedTime = get(item, 'updatedAt', '')
        const updatedAt = updatedTime ? moment(updatedTime).fromNow() : '-'

        return (
          <div className="text-sm text-gray-500">
            <Tooltip title={updatedTime ? formateDate(moment(updatedTime)) : t('branchDiscount.labels.notAvailable')}>{updatedAt}</Tooltip>
          </div>
        )
      },
    },
  ]
  const [BranchDiscountModal, openBranchDiscountModal] = useModal({
    content: <BranchDiscounts onSuccess={() => fetchBranchDiscounts(1)} />,
    title: t('branchDiscount.resetTitle'),
    dimensions: {
      height: 'auto',
      width: '90vw',
      maxWidth: '500px',
      minHeight: 'auto',
      minWidth: '280px',
    },
  })

  return (
    <Layout
      headerRight={
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-end gap-2 sm:gap-0">
          <button
            onClick={() => {
              openBranchDiscountModal()
            }}
            type="button"
            className="inline-flex text-white items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 bg-dark-600 hover:bg-dark-700 font-medium rounded-md mr-0 sm:mr-3 rtl:sm:mr-0 rtl:sm:ml-3 w-full sm:w-auto"
          >
            {t('branchDiscount.buttons.resetBranchDiscount')}
          </button>
          <div className="mt-1 relative rounded-md shadow-sm flex-grow w-full sm:w-auto">
            <PageFooter currentPage={pagination.currentPage} totalPages={pagination.totalPages} totalRecords={pagination.totalRecords} />
          </div>
        </div>
      }
      title={t('branchDiscount.title')}
    >
      <div className="space-y-4 sm:space-y-6 pt-4 sm:pt-8 pb-8 px-4 sm:px-6">
        {/* Company and Branch Selection */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
          <div>
            <label className="block text-sm font-normal text-gray-700 mb-1">{t('branchDiscount.form.company')}</label>
            <Selection
              title={loadingCompanies ? t('common.loading') : t('branchDiscount.form.selectCompanies')}
              list={companyNames}
              className="w-full"
              onChange={value => handleCompanyChange(value?.id || null)}
              value={selectedCompany || ''}
              disabled={loadingCompanies}
              error={fieldError?.field === 'company'}
            />
            {errorText('company')}
          </div>
          <div>
            <label className="block text-sm font-normal text-gray-700 mb-1">{t('branchDiscount.form.branch')}</label>
            <Selection
              title={loadingBranches ? t('common.loading') : t('branchDiscount.form.selectBranch')}
              list={branches}
              onChange={value => {
                setSelectedBranch(value?.id || null)
                clearError('branch')
              }}
              value={selectedBranch || ''}
              className="w-full"
              disabled={!selectedCompany || loadingBranches}
              error={fieldError?.field === 'branch'}
            />
            {errorText('branch')}
          </div>
        </div>

        {/* Rental Period and Start Date */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
          <div>
            <label className="block text-sm font-normal text-gray-700 mb-1">{t('branchDiscount.form.rentalPeriod')}</label>
            <Selection
              onChange={handleRentTypeChange}
              title={t('branchDiscount.form.selectRentalPeriod')}
              list={rentalTypes}
              value={selectedRentType}
              multiple
              className="w-full"
              error={fieldError?.field === 'rentType'}
            />
            {errorText('rentType')}
          </div>
          <div>
            <label htmlFor="discountStartDate" className="block text-sm font-normal text-gray-700 mb-1">
              {t('branchDiscount.form.startDate')}
            </label>
            <input
              type="datetime-local"
              id="discountStartDate"
              value={discountStartDate}
              onChange={e => {
                setDiscountStartDate(e.target.value)
                clearError('discountStartDate')
              }}
              className={inputClass('discountStartDate')}
            />
            {errorText('discountStartDate')}
          </div>
        </div>

        {/* End Date and Ajarlee Percentage */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
          <div>
            <label htmlFor="discountEndDate" className="block text-sm font-normal text-gray-700 mb-1">
              {t('branchDiscount.form.endDate')}
            </label>
            <input
              type="datetime-local"
              id="discountEndDate"
              min={discountStartDate || undefined}
              value={discountEndDate}
              onChange={e => {
                setDiscountEndDate(e.target.value)
                clearError('discountEndDate')
              }}
              className={inputClass('discountEndDate')}
            />
            {errorText('discountEndDate')}
          </div>
          <div>
            <label htmlFor="ajarlePercentage" className="block text-sm font-normal text-gray-700 mb-1">
              {t('branchDiscount.form.ajarleePercentage')}{' '}
              {companyMaxAjarleePercentage != null && <span className="text-gray-500 text-xs">{t('branchDiscount.form.maxPercentage', { max: companyMaxAjarleePercentage })}</span>}
            </label>
            <input
              type="number"
              id="ajarlePercentage"
              min="0"
              max={companyMaxAjarleePercentage ?? undefined}
              step="0.01"
              value={ajarlePercentage}
              onChange={e => {
                setAjarlePercentage(e.target.value)
                clearError('ajarlePercentage')
              }}
              className={inputClass('ajarlePercentage')}
              placeholder={t('branchDiscount.form.enterPercentage')}
            />
            {errorText('ajarlePercentage')}
          </div>
        </div>

        {/* Company Percentage and Ajarlee Delivery Discount */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
          <div>
            <label htmlFor="companyPercentage" className="block text-sm font-normal text-gray-700 mb-1">
              {t('branchDiscount.form.companyPercentage')}
            </label>
            <input
              type="number"
              id="companyPercentage"
              min="0"
              max="100"
              step="0.01"
              value={companyPercentage}
              onChange={e => {
                setCompanyPercentage(e.target.value)
                clearError('companyPercentage')
              }}
              className={inputClass('companyPercentage')}
              placeholder={t('branchDiscount.form.enterPercentage')}
            />
            {errorText('companyPercentage')}
          </div>
          <div>
            <label htmlFor="ajarleeDeliveryDiscount" className="block text-sm font-normal text-gray-700 mb-1">
              {t('branchDiscount.form.ajarleeDeliveryDiscount')}
            </label>
            <input
              type="number"
              id="ajarleeDeliveryDiscount"
              min="0"
              max="100"
              step="0.01"
              value={ajarleeDeliveryDiscount}
              onChange={e => {
                setAjarleeDeliveryDiscount(e.target.value)
                clearError('ajarleeDeliveryDiscount')
              }}
              className={inputClass('ajarleeDeliveryDiscount')}
              placeholder={t('branchDiscount.form.enterAjarleeDeliveryDiscount')}
            />
            {errorText('ajarleeDeliveryDiscount')}
          </div>
        </div>

        {/* Company Delivery Discount - Full width on mobile */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
          <div>
            <label htmlFor="companyDeliveryDiscount" className="block text-sm font-normal text-gray-700 mb-1">
              {t('branchDiscount.form.companyDeliveryDiscount')}
            </label>
            <input
              type="number"
              id="companyDeliveryDiscount"
              min="0"
              max="100"
              step="0.01"
              value={companyDeliveryDiscount}
              onChange={e => {
                setCompanyDeliveryDiscount(e.target.value)
                clearError('companyDeliveryDiscount')
              }}
              className={inputClass('companyDeliveryDiscount')}
              placeholder={t('branchDiscount.form.enterCompanyDeliveryDiscount')}
            />
            {errorText('companyDeliveryDiscount')}
          </div>
          <div className="hidden sm:block"></div>
        </div>

        {/* Set Discount Button */}
        <div className="mt-3 flex justify-center sm:justify-start">
          <Button disabled={generating} onClick={handleSetDiscount} className="w-full sm:w-auto">
            {generating ? (
              <>
                <svg className="animate-spin h-5 w-5 mr-3 rtl:mr-0 rtl:ml-3 text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path
                    className="opacity-75"
                    fill="white"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                {t('branchDiscount.buttons.setDiscount')}
              </>
            ) : (
              t('branchDiscount.buttons.setDiscount')
            )}
          </Button>
        </div>

        {/* Table Container */}
        <div className="w-full overflow-x-auto">
          <div className="min-w-max">
            {!loading ? (
              <>
                {branchDiscounts?.length === 0 ? (
                  <EmptyState v2 description={t('branchDiscount.messages.noDataFound')} />
                ) : (
                  <Table
                    hasMore={pagination?.currentPage !== pagination?.totalPages}
                    fetchData={() => fetchBranchDiscounts(pagination?.currentPage + 1)}
                    sortBy={filter.sortBy}
                    sortOrder={filter.sortOrder}
                    handleSort={(sortBy, sortOrder) => {
                      setFilter(prevFilter => ({ ...prevFilter, sortBy, sortOrder }))
                    }}
                    data={branchDiscounts}
                    columns={columns}
                  />
                )}
              </>
            ) : (
              <div className="w-full h-[200px] border-0 flex justify-center items-center">
                <LoadingIcon />
              </div>
            )}
          </div>
        </div>
      </div>
      <BranchDiscountModal />
    </Layout>
  )
}

export default BranchDiscount
